import { useState } from 'react';
import { Anchor, Center, Group, Modal, Stack, Text, TextInput, PasswordInput, Button, Paper, Alert, Select } from '@mantine/core';
import { AlertCircle } from 'lucide-react';
import type { TenantOption } from '@bmai/auth-api-client';
import { authClient } from '../../shared/AuthApiClient';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import { SessionExpiryNotifier } from '@/shared/auth/SessionExpiryNotifier';
import { UltraTraceWordmark } from '../../design/UltraTraceLogo';

function currentTenantId(): string {
    return window.location.pathname.split('/').filter(Boolean)[0] ?? '';
}

interface TenantLandingPageProps {
    onAuthenticated: () => void;
}

/**
 * Email + password login. Calls /api/auth/login via auth-api; Keycloak
 * is invisible here. When a user belongs to multiple tenants the server
 * returns a selection list and we re-submit with the chosen tenantId.
 */
export function TenantLandingPage({ onAuthenticated }: TenantLandingPageProps): React.ReactElement {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tenantOptions, setTenantOptions] = useState<TenantOption[] | null>(null);
    const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
    const [sessionExpired] = useState(() => SessionExpiryNotifier.consumeExpiredFlag());
    const [resetOpen, setResetOpen] = useState(false);

    async function submit(tenantId?: string): Promise<void> {
        const trimmedEmail = email.trim().toLowerCase();
        if (!trimmedEmail || !password) {
            setError('Please enter your email and password');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const outcome = await authClient.authSessionClient.login(trimmedEmail, password, tenantId);
            switch (outcome.kind) {
                case 'authenticated':
                    onAuthenticated();
                    return;
                case 'tenant-selection-required':
                    setTenantOptions([...outcome.tenants]);
                    setSelectedTenantId(outcome.tenants[0]?.tenantId ?? null);
                    return;
                case 'invalid-credentials':
                    setError(outcome.message);
                    return;
            }
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    }

    async function handleSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        if (tenantOptions) {
            if (!selectedTenantId) { setError('Select a workspace'); return; }
            await submit(selectedTenantId);
        } else {
            await submit();
        }
    }

    return (
        <Center h="100dvh" p="md">
            <Paper shadow="md" p="xl" radius="md" withBorder w="100%" maw={420}>
                <Stack>
                    <Center>
                        <Stack align="center" gap="md">
                            <UltraTraceWordmark size={40} title="UltraTrace" />
                            <Text c="dimmed" size="sm">Sign in to your account</Text>
                        </Stack>
                    </Center>
                    {error ?
                        <Alert variant="light" color="red" icon={<AlertCircle size={16} />}>
                            {error}
                        </Alert>
                    : sessionExpired ?
                        <Alert variant="light" color="yellow" icon={<AlertCircle size={16} />}>
                            Your session expired — please sign in again.
                        </Alert>
                    :   null}
                    <form onSubmit={handleSubmit} method="post" action="/_auth/api/auth/login">
                        <Stack>
                            <TextInput
                                label="Email"
                                placeholder="you@company.com"
                                type="email"
                                name="email"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => { setEmail(e.currentTarget.value); setError(''); }}
                                autoFocus
                                disabled={loading || !!tenantOptions}
                            />
                            <PasswordInput
                                label="Password"
                                placeholder="Your password"
                                name="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => { setPassword(e.currentTarget.value); setError(''); }}
                                disabled={loading || !!tenantOptions}
                            />
                            {tenantOptions && (
                                <Select
                                    label="Workspace"
                                    placeholder="Select a workspace"
                                    value={selectedTenantId}
                                    onChange={setSelectedTenantId}
                                    data={tenantOptions.map((t) => ({ value: t.tenantId, label: t.name }))}
                                />
                            )}
                            <Button type="submit" fullWidth loading={loading}>
                                {tenantOptions ? 'Continue' : 'Sign In'}
                            </Button>
                            {!tenantOptions && (
                                <Group justify="flex-end">
                                    <Anchor size="xs" c="dimmed" onClick={() => setResetOpen(true)}>Forgot password?</Anchor>
                                </Group>
                            )}
                        </Stack>
                    </form>
                    {!tenantOptions && (
                        <Center>
                            <Anchor href="/operator/login" size="xs" c="dimmed">
                                Operator sign in
                            </Anchor>
                        </Center>
                    )}
                </Stack>
            </Paper>
            <ForgotPasswordModal opened={resetOpen} onClose={() => setResetOpen(false)} initialEmail={email} />
        </Center>
    );
}

function ForgotPasswordModal({ opened, onClose, initialEmail }: { opened: boolean; onClose: () => void; initialEmail: string }): React.ReactElement {
    const [email, setEmail] = useState(initialEmail);
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    async function send(): Promise<void> {
        const trimmed = email.trim().toLowerCase();
        if (!trimmed) return;
        setBusy(true);
        try {
            await authenticatedFetch('/_auth/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: trimmed, tenantId: currentTenantId() }),
            });
        } catch { void 0; }
        setBusy(false);
        setSent(true);
    }

    return (
        <Modal opened={opened} onClose={onClose} title="Reset your password" centered>
            {sent ? (
                <Stack>
                    <Text size="sm">If an account exists for that email, we&apos;ve sent a link to reset your password.</Text>
                    <Button onClick={onClose}>Done</Button>
                </Stack>
            ) : (
                <Stack>
                    <TextInput label="Email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.currentTarget.value)} autoFocus />
                    <Button loading={busy} onClick={() => void send()}>Send reset link</Button>
                </Stack>
            )}
        </Modal>
    );
}
